<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as JobEditCloseAsFutileDialogFormInitialData,
  ValidData as JobEditCloseAsFutileDialogFormValidData,
} from '@/components/dialogForms/JobEditCloseAsFutileDialogForm.vue';

import { computed, useTemplateRef } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import JobEditCloseAsFutileDialogForm from '@/components/dialogForms/JobEditCloseAsFutileDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isConfirmLoading?: boolean;
  jobId?: number;
  jobDate?: string;
  reason?: string;
  confirm?: (
    formData: JobEditCloseAsFutileDialogFormValidData,
  ) => Promise<unknown>;
}
export type DefProps = typeof props;

const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  jobId: undefined,
  jobDate: undefined,
  reason: undefined,
  confirm: undefined,
});

const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: JobEditCloseAsFutileDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: JobEditCloseAsFutileDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef<HTMLElement>('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<JobEditCloseAsFutileDialogFormInitialData>(() => {
  return {
    reason: props.reason,
  };
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.jobEditCloseAsFutileDialog"
    data-testid="jobEditCloseAsFutileDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        Close job as futile
      </ReportGenerateDialogHeader>
    </template>

    <section ref="main" :class="$style.main">
      <div :class="$style.text">
        Provide the reason for closing this job as futile.
      </div>
      <JobEditCloseAsFutileDialogForm
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
    </section>

    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            :disabled="props.isConfirmLoading"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-check"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            @click="handleConfirmClick"
          >
            Confirm
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.jobEditCloseAsFutileDialog {
  width: variables.$reportGenerateDialogWidth;
}
.text {
  margin: 0 0 math.div(variables.$pageDialogWindowMainWrapperMargin, 2) 0;
}
.dialogFooterMenuList {
  list-style: none;
  margin: 0;
  padding: 0;
  text-align: right;
}
.dialogFooterMenuListItem {
  display: inline;
}
.dialogFooterMenuListItem + .dialogFooterMenuListItem {
  margin-left: 1rem;
}
</style>
